import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: questions, 3: new password
  const [email, setEmail] = useState('');
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/initiate-reset`, { email });
      setQuestions(response.data.questions);
      // Initialize answers array
      setAnswers(response.data.questions.map(q => ({ question: q, answer: '' })));
      setStep(2);
      toast.success('Please answer your security questions');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate password reset');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswersSubmit = async (e) => {
    e.preventDefault();
    
    // Check that at least 2 answers are provided
    const filledAnswers = answers.filter(a => a.answer.trim() !== '');
    if (filledAnswers.length < 2) {
      toast.error('Please answer at least 2 security questions');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-answers`, {
        email,
        answers: filledAnswers
      });
      setResetToken(response.data.reset_token);
      setStep(3);
      toast.success('Security questions verified!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Incorrect answers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 10) {
      toast.error('Password must be at least 10 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        reset_token: resetToken,
        new_password: newPassword
      });
      toast.success('Password reset successfully! Please login with your new password.');
      navigate('/auth');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen-safe flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-pink-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/auth')}
              className="p-0 h-auto"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Login
            </Button>
          </div>
          <CardTitle>Reset Password</CardTitle>
          <CardDescription>
            {step === 1 && 'Enter your email to begin password reset'}
            {step === 2 && 'Answer at least 2 of your security questions'}
            {step === 3 && 'Create a new password'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 1 && (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Please wait...' : 'Continue'}
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleAnswersSubmit} className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Answer at least <strong>2 out of 3</strong> questions correctly
              </p>
              {questions.map((question, index) => (
                <div key={index}>
                  <Label htmlFor={`answer-${index}`}>{question}</Label>
                  <Input
                    id={`answer-${index}`}
                    type="text"
                    placeholder="Your answer"
                    value={answers[index]?.answer || ''}
                    onChange={(e) => {
                      const newAnswers = [...answers];
                      newAnswers[index] = { question, answer: e.target.value };
                      setAnswers(newAnswers);
                    }}
                  />
                </div>
              ))}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Verifying...' : 'Verify Answers'}
              </Button>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={handlePasswordReset} className="space-y-4">
              <div>
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum 10 characters, 3+ character types
                </p>
              </div>
              <div>
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}
          <p className="text-xs text-gray-500 border-t border-gray-100 pt-3 mt-6" data-testid="forgot-password-support-contact">
            Need help accessing your account? Contact Celey Support at{' '}
            <a href="mailto:customersupport@celeyapp.com" className="text-emerald-600 underline hover:text-emerald-700" data-testid="support-email-link">
              customersupport@celeyapp.com
            </a>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
